import { useState, type FormEvent } from "react"
import Icon from "@/v2-ui/icon"
import Input from "@/v2-ui/input"
import Badge from "@/v2-backoffice-client/components/badge"
import Button, {
  INPUT_ALIGNED_BUTTON_CLASSES,
} from "@/v2-backoffice-client/components/button"
import type { LayerServiceMetadata } from "@/v2-backoffice-client/layer/service/layer.service.metadata"

export type LayerCreateSourceBarProps = {
  isLoading: boolean
  metadata: LayerServiceMetadata | null
  error: string | null
  /** Probes the entered url (Enter or the Fetch button). */
  onProbe(url: string): void
}

/**
 * The create flow's entry point: paste the layer-server source url, fetch
 * its metadata, see what the service is at a glance. The summary row shows
 * the probe's verdict (type, geometry, field/sublayer counts) — the
 * prefills built from it land in the identity form and the editor.
 */
function LayerCreateSourceBar(props: LayerCreateSourceBarProps) {
  const { isLoading, metadata, error, onProbe } = props

  const [url, setUrl] = useState("")

  function submit(event: FormEvent) {
    event.preventDefault()
    if (url.trim()) onProbe(url.trim())
  }

  return (
    <div className="flex shrink-0 flex-col gap-2">
      <form className="flex items-center gap-2" onSubmit={submit}>
        <Input
          className="flex-1 font-mono text-xs"
          placeholder="https://…/rest/services/…/FeatureServer/0 or a WMS endpoint"
          value={url}
          autoFocus
          onChange={(event) => setUrl(event.target.value)}
        />
        <Button
          type="submit"
          size="sm"
          className={INPUT_ALIGNED_BUTTON_CLASSES}
          iconSrc="search"
          isLoading={isLoading}
          disabled={!url.trim()}
          data-feature-code="UI_BackofficeLayers_Create_Probe"
        >
          Fetch metadata
        </Button>
      </form>

      {error && (
        <p className="flex items-start gap-1.5 rounded-md border border-destructive/40 bg-destructive/5 px-2.5 py-1.5 text-xs">
          <Icon src="triangle-warning" size={12} className="mt-px shrink-0" />
          {error} You can still configure the layer manually below.
        </p>
      )}

      {metadata && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          <Icon src="check-circle" size={12} className="text-primary" />
          <span className="font-medium">{metadata.title}</span>
          <Badge variant="outline" size="tiny">
            {metadata.kind === "wms" ? "WMS" : "ArcGIS REST"}
          </Badge>
          <Badge variant="outline" size="tiny">
            {metadata.layerTypeCode}
          </Badge>
          {metadata.geometryType && (
            <Badge variant="outline" size="tiny">
              {metadata.geometryType.replace("esriGeometry", "").toLowerCase()}
            </Badge>
          )}
          {metadata.fields.length > 0 && (
            <Badge variant="outline" size="tiny">
              {metadata.fields.length} fields
            </Badge>
          )}
          {metadata.sublayers.length > 0 && (
            <Badge variant="outline" size="tiny">
              {metadata.sublayers.length} sublayers
            </Badge>
          )}
        </div>
      )}
    </div>
  )
}

export default LayerCreateSourceBar
